import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { PassPostProcess as BABYLON_PassPostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/passPostProcess.js';
import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { ThinTexture as BABYLON_ThinTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/thinTexture.js';
import { LiteRenderTarget } from './utility/LiteRenderTarget.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { WebGLRenderTargetWrapper as BABYLON_WebGLRenderTargetWrapper } from '@inter-ikea-betrakta-babylonjs/core/Engines/WebGL/webGLRenderTargetWrapper.js';
export var ViewerFramebufferType;
(function (ViewerFramebufferType) {
    ViewerFramebufferType[ViewerFramebufferType["RGBA8"] = 0] = "RGBA8";
    ViewerFramebufferType[ViewerFramebufferType["R11_G11_B10F"] = 1] = "R11_G11_B10F";
    ViewerFramebufferType[ViewerFramebufferType["RGBA16F"] = 2] = "RGBA16F";
    ViewerFramebufferType[ViewerFramebufferType["RGBA32F"] = 3] = "RGBA32F";
})(ViewerFramebufferType || (ViewerFramebufferType = {}));
function get_babylon_texture_type(type) {
    if (type === ViewerFramebufferType.R11_G11_B10F) {
        return BABYLON_Engine.TEXTURETYPE_UNSIGNED_INT_10F_11F_11F_REV;
    }
    else if (type === ViewerFramebufferType.RGBA16F) {
        return BABYLON_Engine.TEXTURETYPE_HALF_FLOAT;
    }
    else if (type === ViewerFramebufferType.RGBA32F) {
        return BABYLON_Engine.TEXTURETYPE_FLOAT;
    }
    else if (type === ViewerFramebufferType.RGBA8) {
        return BABYLON_Engine.TEXTURETYPE_UNSIGNED_BYTE;
    }
    else {
        console.error('Missing format conversion for ViewerFramebufferType: ' + type);
        return BABYLON_Engine.TEXTURETYPE_UNSIGNED_BYTE;
    }
}
function get_babylon_texture_format(type) {
    if (type === ViewerFramebufferType.R11_G11_B10F) {
        return BABYLON_Engine.TEXTUREFORMAT_RGB;
    }
    else if (type === ViewerFramebufferType.RGBA8 ||
        type === ViewerFramebufferType.RGBA16F ||
        type === ViewerFramebufferType.RGBA32F) {
        return BABYLON_Engine.TEXTUREFORMAT_RGBA;
    }
    else {
        console.error('Missing format conversion for ViewerFramebufferType: ' + type);
        return BABYLON_Engine.TEXTUREFORMAT_RGBA;
    }
}
function get_opengl_texture_format_sized(gl, type) {
    if (type === ViewerFramebufferType.R11_G11_B10F) {
        return gl.R11F_G11F_B10F;
    }
    else if (type === ViewerFramebufferType.RGBA16F) {
        return gl.RGBA16F;
    }
    else if (type === ViewerFramebufferType.RGBA32F) {
        return gl.RGBA32F;
    }
    else if (type === ViewerFramebufferType.RGBA8) {
        return gl.RGBA8;
    }
    else {
        console.error('Missing format conversion for ViewerFramebufferType: ' + type);
        return gl.RGBA8;
    }
}
/**
 * A class used to bypass Babylon's depth renderer, to allow for much greater efficiency.
 * Babylon's depth renderer actually renders the entire scene into its own color buffer with depth data, which is very wasteful.
 * When rendering to an offscreen framebuffer (the point of the Framebuffer class), with WebGL 2 we can just use the depth data
 * from our forward pass, instead of re-rendering everything. And that's what this class does.
 *
 * With this class we can also choose depth format freely (settled for 24-bits for now). With Babylon's render targets you only get 16 bits or 32F, where the former is not enough, and the latter is wasteful.
 */
export class IntegratedDepthRenderer {
    constructor(engine, render_target, msaa_samples) {
        var _a;
        this._engine = engine;
        this._render_target = render_target;
        this._msaa_samples = msaa_samples;
        this._depth_sized_format = engine._gl.DEPTH24_STENCIL8;
        const gl = engine._gl;
        const width = render_target.getRenderWidth();
        const height = render_target.getRenderHeight();
        // Create and inject a depth renderbuffer into the Babylon render target (framebuffer).
        // According to the spec, createRenderbuffer can't return null.
        this._depth_render_buffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, this._depth_render_buffer);
        if (this._msaa_samples > 1) {
            gl.renderbufferStorageMultisample(gl.RENDERBUFFER, msaa_samples, this._depth_sized_format, width, height);
        }
        else {
            gl.renderbufferStorage(gl.RENDERBUFFER, this._depth_sized_format, width, height);
        }
        gl.bindRenderbuffer(gl.RENDERBUFFER, null);
        // Attaching the renderbuffer to Babylon's framebuffer
        engine.bindFramebuffer(this._render_target.renderTarget);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.RENDERBUFFER, this._depth_render_buffer);
        const opt = {};
        this._internal_depth_texture = engine._createInternalTexture({ width: width, height: height }, opt);
        this._depth_texture_wrapper = new BABYLON_ThinTexture(this._internal_depth_texture);
        // Create the depth texture meant to be read by shaders.
        gl.bindTexture(gl.TEXTURE_2D, this._internal_depth_texture._hardwareTexture.underlyingResource);
        gl.texImage2D(gl.TEXTURE_2D, 0, engine._gl.DEPTH24_STENCIL8, width, height, 0, gl.DEPTH_STENCIL, gl.UNSIGNED_INT_24_8, null);
        // Set sampler parameters (filtering and wrapping), required for the texture to be complete.
        // Force nearest sampling as interpolating non-linear depth is not something you usually want to do. And not sure about hardware support.
        // It's useful for shadow mapping, but that's not what this is for.
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        // Clamp to edge, can't think of any case where you'd want a tiling depth texture.
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        // Attach the depth texture to a simple framebuffer (used so we have something to blit the renderbuffer onto)
        this._depth_blit_fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, this._depth_blit_fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, (_a = this._internal_depth_texture._hardwareTexture) === null || _a === void 0 ? void 0 : _a.underlyingResource, 0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    dispose() {
        const gl = this._engine._gl;
        gl.deleteFramebuffer(this._depth_blit_fbo);
        this._depth_texture_wrapper.dispose();
        this._internal_depth_texture.dispose();
        gl.deleteRenderbuffer(this._depth_render_buffer);
    }
    /**
     * This function makes the depth data from the forward pass readable from the depth texture.
     * Call after rendering the main render target, before needing the depth texture.
     */
    prepare_depth() {
        const width = this._render_target.getRenderWidth();
        const height = this._render_target.getRenderHeight();
        const gl = this._engine._gl;
        this._engine.bindFramebuffer(this._render_target.renderTarget);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._depth_blit_fbo);
        gl.blitFramebuffer(0, 0, width, height, 0, 0, width, height, gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT, gl.NEAREST);
    }
    blit_depth_and_stencil_to_render_target(rt) {
        const width = this._render_target.getRenderWidth();
        const height = this._render_target.getRenderHeight();
        const gl = this._engine._gl;
        this._engine.bindFramebuffer(rt.renderTarget);
        if (this._render_target.renderTarget instanceof BABYLON_WebGLRenderTargetWrapper) {
            const msaa_framebuffer = this._render_target.renderTarget._MSAAFramebuffer;
            const normal_framebuffer = this._render_target.renderTarget._framebuffer;
            gl.bindFramebuffer(gl.READ_FRAMEBUFFER, msaa_framebuffer || normal_framebuffer);
            gl.blitFramebuffer(0, 0, width, height, 0, 0, width, height, gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT, gl.NEAREST);
        }
        else if (this._render_target.renderTarget === null) {
            // Do nothing?
        }
        else {
            throw new Error("Invalid render target wrapper type!");
        }
    }
    resize(width, height) {
        const gl = this._engine._gl;
        gl.bindRenderbuffer(gl.RENDERBUFFER, this._depth_render_buffer);
        if (this._msaa_samples > 1) {
            gl.renderbufferStorageMultisample(gl.RENDERBUFFER, this._msaa_samples, this._depth_sized_format, width, height);
        }
        else {
            gl.renderbufferStorage(gl.RENDERBUFFER, this._depth_sized_format, width, height);
        }
        gl.bindRenderbuffer(gl.RENDERBUFFER, null);
        // This may seem odd, but Babylon kills its framebuffers on resize, so we must re-attach our custom render buffer.
        // If that ever changes in the future, this will error (can't attach a renderbuffer to multiple framebuffers simultaneously)
        // NOTE: Check for error on Babylon update
        this._engine.bindFramebuffer(this._render_target.renderTarget);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.RENDERBUFFER, this._depth_render_buffer);
        gl.bindTexture(gl.TEXTURE_2D, this._internal_depth_texture._hardwareTexture.underlyingResource);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH24_STENCIL8, width, height, 0, gl.DEPTH_STENCIL, gl.UNSIGNED_INT_24_8, null);
    }
    get_depth_texture() {
        return this._depth_texture_wrapper;
    }
}
const ext_webgl2_float_texture_color_buffer = 'EXT_color_buffer_float';
export class Framebuffer {
    static is_float_framebuffer_supported(engine) {
        const is_webgl_1 = engine.webGLVersion === 1;
        const gl = engine._gl;
        return !is_webgl_1 && gl.getExtension(ext_webgl2_float_texture_color_buffer) !== null;
    }
    static is_depth_renderer_supported(engine) {
        const is_webgl_1 = engine.webGLVersion === 1;
        // We could theoretically check for extensions here that would allow us to support our required depth precision and blitting.
        return !is_webgl_1;
    }
    /**
     * A function that helps you find the closest number of MSAA samples that is less or equal to the wanted samples.
     * @param wanted_msaa_samples How many MSAA samples you ideally want
     * @param use_float_buffer If you will be rendering to a float framebuffer (if true, assumes Framebuffer.is_float_framebuffer_supported() is true).
     * @returns The number of MSAA samples supported
     */
    static get_supported_msaa_samples(engine, wanted_msaa_samples, type) {
        const gl = engine._gl;
        // Check if supported format
        const is_webgl_1 = engine.webGLVersion === 1;
        if (!is_webgl_1) {
            const texture_format = get_opengl_texture_format_sized(gl, type);
            const supported_msaa_samples = gl.getInternalformatParameter(gl.RENDERBUFFER, texture_format, gl.SAMPLES);
            if (supported_msaa_samples !== null) {
                let supported_msaa = 1;
                for (let i = 0; i < supported_msaa_samples.length; i++) {
                    if (supported_msaa_samples[i] <= wanted_msaa_samples) {
                        supported_msaa = Math.max(supported_msaa, supported_msaa_samples[i]);
                    }
                }
                return supported_msaa;
            }
            else {
                // This potentially means that this format is not supported at all. The documentation is vague on what getInternalformatParameter actually returns MSAA is not supported.
                // From testing, it seems that it returns an empty object when MSAA is not supported but it still is color-renderable, and returns null when it's not a supported color-renderable format.
                // But to be on the safe side, we assume that both null and empty object both mean that no MSAA is supported. It's not up to this function whether to determine if it's a color-renderable format or not.
                return 1;
            }
        }
        else {
            // If we're on WebGL 1, we have no way of checking supported MSAA samples other than creating framebuffers and looking for errors?
            // TODO? For now we just eat what the user gives us.
            return wanted_msaa_samples;
        }
    }
    /**
     *
     * @param scene The Babylon scene
     * @param width width of the framebuffer
     * @param height height of the framebuffer
     * @param camera the scene's camera to apply the framebuffer to (Babylon architecture)
     * @param msaa_samples Number of MSAA samples. 1 == no MSAA
     * @param use_float_buffer Whether to enable float buffer for HDR rendering. Check for support with Framebuffer.is_float_framebuffer_supported()
     * @param enable_depth_renderer Whether to enable the depth renderer or not. Check for support with Framebuffer.is_depth_renderer_supported
     */
    constructor(scene, type, width, height, camera, msaa_samples, enable_depth_renderer) {
        this._scene = scene;
        this.type = type;
        // Sanity check. Should have been assured by the user calling this constructor.
        if (this.is_float_render_target() && !Framebuffer.is_float_framebuffer_supported(scene.getEngine())) {
            console.error('Viewer Framebuffer created with unsupported float textures.');
        }
        // The caller of this constructor should have made sure the number of MSAA samples is supported.
        // But we do a sanity check just-in-case, and report an error.
        if (msaa_samples !== Framebuffer.get_supported_msaa_samples(this._scene.getEngine(), msaa_samples, type)) {
            console.error('Viewer Framebuffer created with unsupported number of MSAA samples.');
        }
        this._msaa_samples = msaa_samples;
        const texture_type = get_babylon_texture_type(type);
        const is_srgb = texture_type === BABYLON_Engine.TEXTURETYPE_UNSIGNED_BYTE;
        // WebGL1 doesn't guarantee RGB8 framebuffers, so we must use RGBA.
        const texture_format = get_babylon_texture_format(type);
        // WebGL1 doesn't seem to support bilinear sampling on framebuffer attachments, so here we branch!
        // It creates ugly pixels when sub-/super-sampling on Views, but that's okay since WebGL1 is on life support.
        const sampler_filter_type = scene.getEngine().webGLVersion === 2
            ? BABYLON_Texture.BILINEAR_SAMPLINGMODE
            : BABYLON_Texture.NEAREST_SAMPLINGMODE;
        // The depth renderer injects its own depth render buffer into the render target,
        // so if we don't use the depth renderer, we let Babylon create its own depth buffer.
        const generate_depth = !enable_depth_renderer;
        const generate_stencil = !enable_depth_renderer;
        this._render_target = new LiteRenderTarget('viewer-fbo', { width: width, height: height }, scene, false, undefined, texture_type, false, sampler_filter_type, generate_depth, generate_stencil, false, texture_format, false, msaa_samples, undefined, false, is_srgb);
        this._render_target.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._render_target.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        // We have to use a custom camera for our blit postprocess, for some Babylon reason. Otherwise it breaks.
        this.postprocess_camera = new BABYLON_Camera('viewer-fbo-postprocess', new BABYLON_Vector3());
        this._render_target.ignoreCameraViewport = true;
        camera.outputRenderTarget = this._render_target;
        this._render_target.activeCamera = camera;
        this._blit_effect = new BABYLON_PassPostProcess('viewer-fbo-blit', { width: width, height: height }, this.postprocess_camera);
        this._blit_effect.forceFullscreenViewport = true;
        this._blit_effect.onApply = (effect) => {
            effect.setTexture('textureSampler', this._render_target);
        };
        if (enable_depth_renderer) {
            // Sanity check, but should be assured by the caller of this constructor
            if (!Framebuffer.is_depth_renderer_supported(this._scene.getEngine())) {
                console.error('Viewer Framebuffer creating unsupported depth renderer');
            }
            this._depth_renderer = new IntegratedDepthRenderer(scene.getEngine(), this._render_target, msaa_samples);
        }
        else {
            this._depth_renderer = null;
        }
    }
    resize(width, height) {
        if (this._render_target.getRenderWidth() !== width || this._render_target.getRenderHeight() !== height) {
            this._render_target.resize({ width: width, height: height });
            if (this._depth_renderer !== null) {
                this._depth_renderer.resize(width, height);
            }
        }
    }
    is_float_render_target() {
        return this.type === ViewerFramebufferType.R11_G11_B10F ||
            this.type === ViewerFramebufferType.RGBA16F ||
            this.type === ViewerFramebufferType.RGBA32F;
    }
    get_texture() {
        return this._render_target;
    }
    get_depth_texture() {
        if (this._depth_renderer === null) {
            return null;
        }
        else {
            return this._depth_renderer.get_depth_texture();
        }
    }
    get_render_target() {
        return this._render_target;
    }
    get_msaa_samples() {
        return this._msaa_samples;
    }
    get_width() {
        return this._render_target.getRenderWidth();
    }
    get_height() {
        return this._render_target.getRenderHeight();
    }
    dispose() {
        var _a;
        this._render_target.dispose();
        this.postprocess_camera.dispose();
        this._blit_effect.dispose();
        (_a = this._depth_renderer) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    prepare_depth_buffer_texture() {
        if (this._depth_renderer !== null) {
            this._depth_renderer.prepare_depth();
        }
    }
    blit_depth_stencil_to_texture(rt) {
        var _a;
        (_a = this._depth_renderer) === null || _a === void 0 ? void 0 : _a.blit_depth_and_stencil_to_render_target(rt);
    }
    blit(destination) {
        this._scene.postProcessManager.directRender([this._blit_effect], destination, true);
    }
    bind() {
        this._render_target._bindFrameBuffer();
        this._scene.getEngine().setStencilBuffer(true);
    }
}
// Mask that can be written to block objects from rendering bloom
Framebuffer.STENCIL_BLOCK_BLOOM_MASK = 0b00000001;
//# sourceMappingURL=Framebuffer.js.map